<template>
  <div class="snelstart-manage">
    <div v-if="loading" class="snelstart-manage__loading">
      <LoadingSpinner />
    </div>

    <template v-else>
      <div
        class="snelstart-manage__banner"
        :class="
          isActive
            ? 'snelstart-manage__banner--success'
            : 'snelstart-manage__banner--info'
        "
      >
        <BaseIcon
          :name="isActive ? 'check-circle' : 'information-circle'"
          size="14"
        />
        <span>
          {{
            isActive
              ? $t('snelstart.status.active_message')
              : $t('snelstart.status.empty_message')
          }}
        </span>
      </div>

      <p v-if="status.company_name" class="snelstart-manage__company">
        {{ $t('snelstart.settings.company') }}:
        <strong>{{ status.company_name }}</strong>
      </p>

      <div class="snelstart-manage__fields">
        <TextInput
          id="snelstart-connection-key"
          type="password"
          :label="$t('snelstart.settings.connection_key')"
          :info="$t('snelstart.settings.connection_key_info')"
          v-model="form.connection_key"
          :error="errors.connection_key"
          :placeholder="
            status.has_connection_key
              ? $t('snelstart.settings.connection_key_placeholder_existing')
              : '••••••••'
          "
        />

        <SelectInput
          id="snelstart-grootboek-hoog"
          :label="$t('snelstart.settings.grootboek_hoog')"
          v-model="form.grootboek_omzet_hoog"
          :options="grootboekOptions"
          :disabled="grootboekOptions.length <= 1"
        />

        <SelectInput
          id="snelstart-grootboek-laag"
          :label="$t('snelstart.settings.grootboek_laag')"
          v-model="form.grootboek_omzet_laag"
          :options="grootboekOptions"
          :disabled="grootboekOptions.length <= 1"
        />

        <SelectInput
          id="snelstart-grootboek-nul"
          :label="$t('snelstart.settings.grootboek_nul')"
          v-model="form.grootboek_omzet_nul"
          :options="grootboekOptions"
          :disabled="grootboekOptions.length <= 1"
        />

        <div class="snelstart-manage__toggle">
          <ToggleInput
            id="snelstart-is-active"
            :label="$t('snelstart.settings.is_active')"
            v-model="form.is_active"
          />
          <p class="snelstart-manage__toggle-hint">
            {{ $t('snelstart.settings.is_active_info') }}
          </p>
        </div>
      </div>

      <p class="snelstart-manage__help">
        {{ $t('snelstart.settings.help') }}
      </p>

      <div class="snelstart-manage__actions">
        <Btn
          v-if="isConnected"
          variant="danger"
          :loading="disconnecting"
          @click="handleDisconnect"
        >
          {{ $t('snelstart.actions.disconnect') }}
        </Btn>
        <div v-else />

        <Btn variant="primary" :loading="saving" @click="handleSave">
          {{
            isConnected
              ? $t('snelstart.actions.save')
              : $t('snelstart.actions.connect')
          }}
        </Btn>
      </div>
    </template>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, onMounted, reactive, ref } from 'vue';

  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';

  import snelstartService from '@/services/snelstartService';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';

  export default {
    name: 'SnelstartManageModal',

    components: {
      Btn,
      BaseIcon,
      LoadingSpinner,
      TextInput,
      SelectInput,
      ToggleInput,
    },

    props: {
      modalId: { type: [String, Number], default: null },
      onUpdated: { type: Function, default: null },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const loading = ref(false);
      const saving = ref(false);
      const disconnecting = ref(false);
      const status = ref({
        connected: false,
        platform_configured: true,
        has_connection_key: false,
      });
      const grootboekOptions = ref([]);
      const errors = reactive({ connection_key: '' });

      const form = reactive({
        connection_key: '',
        grootboek_omzet_hoog: '',
        grootboek_omzet_laag: '',
        grootboek_omzet_nul: '',
        is_active: true,
      });

      const isConnected = computed(() => !!status.value.connected);
      const isActive = computed(
        () => isConnected.value && form.is_active
      );

      const populateFromStatus = (s) => {
        status.value = s || {};
        form.grootboek_omzet_hoog = s?.grootboek_omzet_hoog || '';
        form.grootboek_omzet_laag = s?.grootboek_omzet_laag || '';
        form.grootboek_omzet_nul = s?.grootboek_omzet_nul || '';
        form.is_active = s?.is_active !== false;
      };

      const mapGrootboekOptions = (list) => {
        const empty = { value: '', label: $t('snelstart.settings.grootboek_empty') };
        const mapped = (list || []).map((g) => ({
          value: g.id,
          label: [g.nummer || g.number, g.omschrijving || g.description || g.naam]
            .filter(Boolean)
            .join(' — '),
        }));
        return [empty, ...mapped];
      };

      const loadGrootboeken = async () => {
        try {
          const data = await snelstartService.listGrootboekrekeningen();
          grootboekOptions.value = mapGrootboekOptions(data?.grootboekrekeningen);
        } catch {
          grootboekOptions.value = mapGrootboekOptions([]);
        }
      };

      const validate = () => {
        errors.connection_key = '';
        if (!isConnected.value && !form.connection_key?.trim()) {
          errors.connection_key = $t('snelstart.settings.connection_key_info');
          return false;
        }
        return true;
      };

      const handleSave = async () => {
        if (!validate()) return;

        saving.value = true;
        try {
          const payload = {
            is_active: form.is_active,
            grootboek_omzet_hoog: form.grootboek_omzet_hoog || null,
            grootboek_omzet_laag: form.grootboek_omzet_laag || null,
            grootboek_omzet_nul: form.grootboek_omzet_nul || null,
          };

          let result;
          if (!isConnected.value || form.connection_key.trim()) {
            result = await snelstartService.connect({
              connection_key: form.connection_key.trim(),
              ...payload,
            });
          } else {
            result = await snelstartService.update(payload);
          }

          populateFromStatus(result?.status || (await snelstartService.getStatus()));
          form.connection_key = '';
          await loadGrootboeken();

          toastStore.addToast({
            type: 'success',
            message: $t('snelstart.save.success'),
          });

          if (typeof props.onUpdated === 'function') {
            await props.onUpdated();
          }
        } catch (error) {
          const msg =
            error?.response?.data?.error ||
            error.message ||
            $t('snelstart.save.error');
          toastStore.addToast({ type: 'error', message: msg });
        } finally {
          saving.value = false;
        }
      };

      const handleDisconnect = () => {
        const confirmId = uiStore.addModal(null, {
          title: $t('snelstart.disconnect.title'),
          description: $t('snelstart.disconnect.description'),
          status: 'danger',
          submitText: $t('snelstart.disconnect.confirm'),
          cancelText: $t('common.cancel'),
          onSubmit: async () => {
            disconnecting.value = true;
            try {
              await snelstartService.disconnect();
              uiStore.closeModal(confirmId);
              if (props.modalId) uiStore.closeModal(props.modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('snelstart.disconnect.success'),
              });
              if (typeof props.onUpdated === 'function') {
                await props.onUpdated();
              }
            } catch (error) {
              uiStore.closeModal(confirmId);
              const msg =
                error?.response?.data?.error ||
                error.message ||
                $t('snelstart.disconnect.error');
              toastStore.addToast({ type: 'error', message: msg });
            } finally {
              disconnecting.value = false;
            }
          },
          onCancel: () => uiStore.closeModal(confirmId),
        });
      };

      onMounted(async () => {
        loading.value = true;
        try {
          const s = await snelstartService.getStatus();
          populateFromStatus(s);
          if (s?.connected) {
            await loadGrootboeken();
          } else {
            grootboekOptions.value = mapGrootboekOptions([]);
          }
        } catch (error) {
          console.error('Failed to load SnelStart status:', error);
        } finally {
          loading.value = false;
        }
      });

      return {
        loading,
        saving,
        disconnecting,
        status,
        form,
        errors,
        grootboekOptions,
        isConnected,
        isActive,
        handleSave,
        handleDisconnect,
      };
    },
  };
</script>

<style scoped lang="scss">
  .snelstart-manage {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem 0;
    }

    &__banner {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      line-height: 1.45;

      &--info {
        background: var(--slate-50);
        color: var(--slate-700);
      }

      &--success {
        background: var(--green-50);
        color: var(--green-800);
      }
    }

    &__company {
      margin: 0;
      font-size: 0.875rem;
      color: var(--slate-600);
    }

    &__fields {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__toggle {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__toggle-hint {
      margin: 0;
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.4;
    }

    &__help {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
      line-height: 1.45;
      padding-top: 0.25rem;
      border-top: 1px solid var(--slate-100);
    }

    &__actions {
      display: flex;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
      padding-top: 0.25rem;
    }
  }
</style>
